import EditIcon from "@mui/icons-material/Edit";
import { useCallback, useRef, useState } from "react";
import { EMAIL_FIELD_LIMITS, QUICK_TEMPLATES, TOKENS } from "@/constants/campaignBuilder";
import type { Stage3EmailState } from "@/context/CampaignBuilderContext";
import { useSubjectAtMention } from "@/hooks/useSubjectAtMention";
import type { RichEditorRef } from "@/interfaces/CampaignBuilder";
import type { EmailMessageSection } from "@/types/types";
import { insertAtCursor } from "@/utils/messagePanel-helper";
import { QuickTemplateCard } from "./QuickTemplateCard";
import RichEditor from "./RichEditor";
import { TokenDropdown } from "./TokenDropdown";
import { TokenMenu } from "./TokenMenu";

const sectionEditBtnStyle: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--color-smoke)",
  borderRadius: "4px",
  padding: "3px",
  lineHeight: "1",
  cursor: "pointer",
  fontSize: "16px",
};

interface MessagePanelProps {
  state: Stage3EmailState;
  onChange: (v: Partial<Stage3EmailState>) => void;
}

export default function MessagePanel({ state, onChange }: MessagePanelProps) {
  const messageRef = useRef<RichEditorRef>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const signatureInputRef = useRef<HTMLInputElement>(null);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const handleMessageChange = useCallback(
    (html: string) => onChangeRef.current({ messageHtml: html }),
    [],
  );

  const subjectCharCount = state.subject.length;
  const [expandedSections, setExpandedSections] = useState<Record<EmailMessageSection, boolean>>({
    subject: false,
    message: false,
    signature: false,
  });
  const [selectedTemplateIdx, setSelectedTemplateIdx] = useState<number | null>(null);

  const toggleSection = (section: EmailMessageSection) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const {
    subjectAtMention,
    handleSubjectChange,
    handleSubjectKeyDown,
    insertSubjectToken,
    dismissAtMention: dismissSubjectAtMention,
  } = useSubjectAtMention(
    subjectInputRef,
    (val) => onChange({ subject: val }),
    EMAIL_FIELD_LIMITS.subject,
  );

  return (
    <div className="flex flex-col gap-0" style={{ padding: 20, boxSizing: "border-box" }}>
      {/* Subject */}
      <div style={{ marginBottom: 20 }}>
        {expandedSections.subject ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-lato text-[13px] font-semibold uppercase tracking-[0.8px] text-rodeo-black">
                  Subject line
                </span>
                <TokenDropdown
                  onInsert={(token) => {
                    const el = subjectInputRef.current;
                    if (el)
                      insertAtCursor(
                        el,
                        token,
                        (v) => onChange({ subject: v }),
                        EMAIL_FIELD_LIMITS.subject,
                      );
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => toggleSection("subject")}
                className="text-dark-dust hover:text-rodeo-black transition-colors"
                style={{
                  ...sectionEditBtnStyle,
                }}
                title="Close"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <div className="relative">
              <input
                id="email-subject"
                ref={subjectInputRef}
                type="text"
                maxLength={EMAIL_FIELD_LIMITS.subject}
                value={state.subject}
                onChange={handleSubjectChange}
                onKeyDown={handleSubjectKeyDown}
                onBlur={dismissSubjectAtMention}
                autoComplete="off"
                placeholder="Enter email subject line"
                className="w-full h-10 px-3.5 border rounded-btn bg-white font-lato text-[14px] text-rodeo-black placeholder:text-graphite outline-none transition-colors border-smoke focus:border-dark-dust"
              />
              {subjectAtMention.active && subjectAtMention.items.length > 0 && (
                <TokenMenu
                  items={subjectAtMention.items}
                  activeIdx={subjectAtMention.activeIdx}
                  onInsert={insertSubjectToken}
                  className="absolute left-0 z-50"
                  style={{ top: "calc(100% + 4px)" }}
                />
              )}
            </div>
            <div className="flex justify-end mt-1">
              <span className="font-lato text-[11px] text-graphite">
                {subjectCharCount} / {EMAIL_FIELD_LIMITS.subject}
              </span>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span className="font-lato text-[13px] font-semibold uppercase tracking-[0.8px] text-rodeo-black">
              Subject line
            </span>
            <button
              type="button"
              onClick={() => toggleSection("subject")}
              className="text-dark-dust hover:text-rodeo-black transition-colors"
              style={{
                ...sectionEditBtnStyle,
              }}
              title="Edit subject line"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* Message */}
      <div style={{ marginBottom: 20 }}>
        {expandedSections.message ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-lato text-[13px] font-semibold uppercase tracking-[0.8px] text-rodeo-black">
                  Message
                </span>
                <TokenDropdown onInsert={(token) => messageRef.current?.insertText(token)} />
              </div>
              <button
                type="button"
                onClick={() => toggleSection("message")}
                className="text-dark-dust hover:text-rodeo-black transition-colors"
                style={{
                  ...sectionEditBtnStyle,
                }}
                title="Close"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <RichEditor
              key={selectedTemplateIdx ?? "default"}
              ref={messageRef}
              initialHtml={state.messageHtml}
              onChange={handleMessageChange}
              maxLength={EMAIL_FIELD_LIMITS.message}
              minHeight="140px"
              placeholder="Enter your message to recipients"
              atMentionTokens={TOKENS}
              showAlign
            />
            <div style={{ marginTop: 16 }}>
              <div
                className="font-lato font-bold text-rodeo-black"
                style={{ fontSize: 13, marginBottom: 12 }}
              >
                Quick Message Templates
              </div>
              {QUICK_TEMPLATES.map((t, i) => (
                <QuickTemplateCard
                  key={t.name}
                  template={t}
                  index={i}
                  selected={selectedTemplateIdx === i}
                  setSelectedTemplateIdx={setSelectedTemplateIdx}
                  onChange={onChange}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span className="font-lato text-[13px] font-semibold uppercase tracking-[0.8px] text-rodeo-black">
              Message
            </span>
            <button
              type="button"
              onClick={() => toggleSection("message")}
              className="text-dark-dust hover:text-rodeo-black transition-colors"
              style={{
                ...sectionEditBtnStyle,
              }}
              title="Edit message"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </div>

      {/* Signature */}
      <div>
        {expandedSections.signature ? (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="font-lato text-[13px] font-semibold uppercase tracking-[0.8px] text-rodeo-black">
                  Signature
                </span>
                <TokenDropdown
                  onInsert={(token) => {
                    const el = signatureInputRef.current;
                    if (el)
                      insertAtCursor(
                        el,
                        token,
                        (v) => onChange({ signatureHtml: v }),
                        EMAIL_FIELD_LIMITS.signature,
                      );
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => toggleSection("signature")}
                className="text-dark-dust hover:text-rodeo-black transition-colors"
                style={{
                  ...sectionEditBtnStyle,
                }}
                title="Close"
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
            <input
              ref={signatureInputRef}
              type="text"
              maxLength={EMAIL_FIELD_LIMITS.signature}
              value={state.signatureHtml}
              onChange={(e) => onChange({ signatureHtml: e.target.value })}
              autoComplete="off"
              placeholder="Add a signature (optional)"
              className="w-full h-10 px-3.5 border rounded-btn bg-white font-lato text-[14px] text-rodeo-black placeholder:text-graphite outline-none transition-colors border-smoke focus:border-dark-dust"
            />
            <div className="flex justify-end mt-1">
              <span className="font-lato text-[11px] text-graphite">
                {state.signatureHtml.length} / {EMAIL_FIELD_LIMITS.signature}
              </span>
            </div>
            <p className="mt-1 font-lato text-[12px] text-graphite leading-snug">
              What you add here is used in your subject line, and anywhere you add the
              personalization tag{" "}
              <code className="font-mono text-[11px] bg-wax px-1 rounded-sm">@Signature</code>.
            </p>
          </>
        ) : (
          <div className="flex items-center justify-between">
            <span className="font-lato text-[13px] font-semibold uppercase tracking-[0.8px] text-rodeo-black">
              Signature
            </span>
            <button
              type="button"
              onClick={() => toggleSection("signature")}
              className="text-dark-dust hover:text-rodeo-black transition-colors"
              style={{
                ...sectionEditBtnStyle,
              }}
              title="Edit signature"
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
